import React, { useState } from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEquals, faPlus, faCartShopping } from '@fortawesome/free-solid-svg-icons';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'reactstrap';

import 'Components/Product/BuyWith/BuyWith.scss';

import BuyWithHitCard from 'Components/Product/BuyWith/BuyWithHitCard';
import productToHit from 'Helpers/Product/ProductToHit';
import { addAll } from 'Redux/ducks/cart';
import SpecialFormattedNumber from 'Components/Utility/Price/SpecialFormattedNumber';
import { currencyCode } from 'Helpers/Geo/data';
import MessageBox from 'Components/Utility/MessageBox';
import useViewport from 'Hooks/useViewport';
import { history } from 'Redux/store';
import { IsControl } from 'Redux/ducks/split-test';

const messages = defineMessages({
  choose_items_to_buy_together: {
    id: 'buywith.choseitemstobuytogether',
    defaultMessage: 'Choose items to buy together',
  },
});

const BuyWith = ({
  product,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { isBelowLg } = useViewport();

  const productCrossPromotedHits = product?.crossPromotedHits || [];
  const productRelatedHits = product?.relatedHits || [];
  const crossPromoteDlcSection = product?.crossPromoteDlcSection || false;
  const cartSlugs = useSelector(state => state?.cart?.allSlugs);

  const relatedHits =
    (crossPromoteDlcSection ? productCrossPromotedHits : productRelatedHits) || [];

  const currentProductHit = productToHit(product);
  let recommendationsToUse = [currentProductHit, ...relatedHits];
  recommendationsToUse = recommendationsToUse
    .filter(hit => hit.slug === currentProductHit.slug || !cartSlugs.includes(hit.slug))
    .slice(0, 3);

  const defaultSelectedProductIds = recommendationsToUse.map(hit => hit?.product_id);

  const [selectedProductIds, setSelectedProductIds] = useState(defaultSelectedProductIds);
  const selectedCount = selectedProductIds.length || 1;

  const isDeslectedAll = selectedProductIds.length === 0;

  const calculatePrices = () => {
    let fullPrice = 0;
    let price = 0;
    let discountPercent = 0;

    const selectedRecommendations =
      recommendationsToUse.filter(hit => selectedProductIds.includes(hit.product_id));

    selectedRecommendations.forEach((recommendation) => {
      fullPrice += recommendation.fullPrice[currencyCode];
      price += recommendation.price[currencyCode];
    });

    discountPercent = (fullPrice - price) / fullPrice;

    return { fullPrice, discountPercent, price };
  };

  const { fullPrice, discountPercent, price } = calculatePrices();
  const hideDiscount = !discountPercent;

  const handleAddSelected = () => {
    dispatch(addAll(selectedProductIds));
    return history.push('/cart');
  };

  if (relatedHits?.length < 2) return false;
  if (isBelowLg) return false;
  if (recommendationsToUse.length < 3) return false;
  if (IsControl('ProductPageBuyWithDLC')) return false;

  return (
    <section className="BuyWith page-section-margin">
      <h2 className="section-title pb-2">
        <FormattedMessage id="buywith.title" defaultMessage="Frequently Bought Together" />
      </h2>

      <div className="BuyWith__content">
        <div className="BuyWith__recommendations">
          {recommendationsToUse.map((hit, i) =>
            <React.Fragment key={hit.slug}>
              <BuyWithHitCard
                lazy
                disableLink={i === 0}
                lgSize={4}
                hit={hit}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
              />
              {(i + 1) < recommendationsToUse.length &&
                <FontAwesomeIcon icon={faPlus} className="BuyWith__row__icon" />
              }

              {(i + 1) === recommendationsToUse.length &&
                <FontAwesomeIcon icon={faEquals} className="BuyWith__row__icon" />
              }
            </React.Fragment>,
          )}
        </div>

        <div className="BuyWith__rightSection">
          {isDeslectedAll ?
            <MessageBox
              type="info"
              message={formatMessage(messages.choose_items_to_buy_together)}
            />

            :
            <React.Fragment>
              <div className="BuyWith__upsellMessage">
                <FormattedMessage
                  id="buywith.selected"
                  defaultMessage="Total Price for {selectedCount, plural, one {# item} other {# items}}:"
                  values={{ selectedCount }}
                />
              </div>
              <div className="BuyWith__PriceContainer">
                <div className="BuyWith__PriceContainer__price">
                  <SpecialFormattedNumber
                    style="currency"
                    value={price}
                  />
                </div>
                {!hideDiscount &&
                  <div className="BuyWith__PriceContainer__wasPrice">
                    <SpecialFormattedNumber
                      style="currency"
                      value={fullPrice}
                    />
                  </div>
                }
                {!hideDiscount &&
                  <div className="BuyWith__PriceContainer__savingPercentage">
                    -<SpecialFormattedNumber value={discountPercent} style="percent" />
                  </div>
                }
              </div>

              <Button
                color="primary"
                size="lg"
                className="cart-btn BuyWith__addAllToCart"
                onClick={handleAddSelected}
              >
                <div className="cart-btn-icon">
                  <FontAwesomeIcon icon={faCartShopping} />
                </div>

                <div className="btn-text-container">
                  {selectedProductIds?.length === recommendationsToUse?.length ? (
                    <FormattedMessage
                      id="buywith.addalltocart"
                      defaultMessage="Add all to Cart"
                    />
                  ) : (
                    <FormattedMessage
                      id="buywith.someitems.addtocart"
                      values={{
                        selected: selectedProductIds?.length || 0,
                      }}
                      defaultMessage="Add {selected} to Cart"
                    />
                  )}
                </div>

              </Button>

            </React.Fragment>
          }
        </div>
      </div>
    </section>
  );
};

BuyWith.propTypes = {
  product: PropTypes.shape({
    crossPromotedHits: PropTypes.arrayOf(PropTypes.shape({})),
    relatedHits: PropTypes.arrayOf(PropTypes.shape({})),
    crossPromoteDlcSection: PropTypes.bool,
  }),
};

BuyWith.defaultProps = {
  product: null,
};

export default BuyWith;
